
import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react';

export type PromptMode = 'custom' | 'langfuse';

export type SelectedPrompt = {
  id?: string;
  title: string;
  content: string;
} | null;

export type LangfusePromptRef = {
  name: string;
  label?: string; // e.g., "production", "staging"
  version?: string; // optional version number
} | null;

type PromptSelectionContextValue = {
  // Mode selection
  promptMode: PromptMode;
  setPromptMode: (mode: PromptMode) => void;
  
  // Custom prompt (existing functionality)
  selectedPrompt: SelectedPrompt;
  setSelectedPrompt: (prompt: SelectedPrompt) => void;
  
  // Langfuse prompt reference (new)
  langfusePromptRef: LangfusePromptRef;
  setLangfusePromptRef: (ref: LangfusePromptRef) => void;
};

const PromptSelectionContext = createContext<PromptSelectionContextValue | null>(null);

export function PromptSelectionProvider({ children }: { children: ReactNode }) {
  const [promptMode, setPromptModeState] = useState<PromptMode>('langfuse');
  const [selectedPrompt, setSelectedPromptState] = useState<SelectedPrompt>(null);
  const [langfusePromptRef, setLangfusePromptRefState] = useState<LangfusePromptRef>(null);

  const setPromptMode = useCallback((mode: PromptMode) => {
    setPromptModeState(mode);
  }, []);

  const setSelectedPrompt = useCallback((prompt: SelectedPrompt) => {
    setSelectedPromptState(prompt);
  }, []);

  const setLangfusePromptRef = useCallback((ref: LangfusePromptRef) => {
    setLangfusePromptRefState(ref);
  }, []);

  const value = useMemo(
    () => ({
      promptMode,
      setPromptMode,
      selectedPrompt,
      setSelectedPrompt,
      langfusePromptRef,
      setLangfusePromptRef,
    }),
    [promptMode, setPromptMode, selectedPrompt, setSelectedPrompt, langfusePromptRef, setLangfusePromptRef]
  );

  return <PromptSelectionContext.Provider value={value}>{children}</PromptSelectionContext.Provider>;
}

export function usePromptSelection() {
  const ctx = useContext(PromptSelectionContext);
  if (!ctx) {
    throw new Error('usePromptSelection must be used within a PromptSelectionProvider');
  }
  return ctx;
}
